import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import { LoaderCircle } from 'lucide-react';
import { useTheme } from '../core/theme-provider';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'text-primary-foreground shadow',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-9 px-8',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, loading = false, ...props }, ref) => {
    const { theme } = useTheme();
    const Comp = asChild ? Slot : 'button';
    const resolvedVariant = variant ?? 'default';
    const themeAwareClassName =
      resolvedVariant === 'default'
        ? theme === 'dark'
          ? 'bg-true-white text-black-base hover:bg-true-white dark:bg-primary-foreground dark:text-black-base dark:hover:bg-grey-900 dark:hover:text-true-white'
          : 'bg-primary text-primary-foreground hover:bg-primary-600'
        : '';

    return (
      <Comp
        className={cn(buttonVariants({ variant: resolvedVariant, size }), themeAwareClassName, className)}
        ref={ref}
        {...props}
      >
        {loading && <LoaderCircle className=" animate-spin" />}
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
